import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check, ChevronRight } from 'lucide-react';
import { MouseGlow } from '@/components/site/MouseGlow';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { FAQList } from '@/components/site/FAQ';
import { CTABand } from '@/components/site/CTABand';
import { FaqSchema } from '@/components/site/Seo';
import { SITE } from '@/data/site';
import { CITIES } from '@/data/cities';
import { PLANS, RETAINERS, MARKET, COST_FACTORS, PRICING_FAQ, PRICING_UPDATED } from '@/data/pricing';

function PricingHero() {
  return (
    <section className="relative overflow-hidden">
      <MouseGlow intensity={0.28} />
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-16 md:pb-24">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-ink-muted">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight size={12} />
          <span className="text-white">Pricing</span>
        </nav>
        <SectionLabel as="h1" num="—" className="mt-8" data-testid="pricing-h1">
          Website Development Cost in Pune, Mumbai &amp; Nashik ({PRICING_UPDATED})
        </SectionLabel>
        <WordReveal
          as="p"
          text="Clear prices. Fixed quotes. No surprise invoices."
          accent="Fixed"
          className="mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-[78px] font-extrabold tracking-tightest leading-[1.02] text-balance max-w-5xl"
        />
        <Reveal delay={450}>
          <p className="mt-10 text-lg md:text-xl text-ink-muted max-w-2xl leading-relaxed">
            What a website, online store or app costs with NioDelta — and how that compares with
            what agencies across Pune, Mumbai and Nashik publicly charge. All prices in INR,
            excluding GST. Every project gets a fixed written quote after a free discovery call.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function PlanCard({ plan, index }) {
  return (
    <Reveal delay={index * 60}>
      <article
        data-testid={`plan-${plan.slug}`}
        className={`h-full flex flex-col rounded-[20px] border p-7 ${plan.featured ? 'border-violet/60 bg-violet/[0.06]' : 'border-white/[0.12] bg-ink-surface'}`}
      >
        <div className="text-[11px] uppercase tracking-[0.24em] text-ink-muted">{plan.bestFor}</div>
        <h3 className="mt-3 text-2xl font-bold tracking-tight">{plan.name}</h3>
        <div className="mt-5">
          <div className="text-sm text-ink-muted">Starting at</div>
          <div className="text-4xl font-bold tracking-tight tabular-nums">{plan.from}</div>
          <div className="mt-1 text-sm text-ink-muted">Typical: {plan.range} · {plan.timeline}</div>
        </div>
        <ul className="mt-6 space-y-2.5 text-sm text-white/85 flex-1">
          {plan.includes.map((item) => (
            <li key={item} className="flex gap-2.5">
              <Check size={16} className="text-violet shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        {plan.link && (
          <Link to={plan.link} className="arrow-link mt-7 inline-flex items-center gap-2 text-sm text-violet">
            About this service <span className="arrow">→</span>
          </Link>
        )}
      </article>
    </Reveal>
  );
}

function Plans() {
  return (
    <section className="relative py-20 md:py-28 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionLabel num="01">Packages</SectionLabel>
        <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
          Website, ecommerce &amp; app development prices
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PLANS.map((p, i) => <PlanCard key={p.slug} plan={p} index={i} />)}
        </div>
      </div>
    </section>
  );
}

function Retainers() {
  return (
    <section className="relative py-20 md:py-28 border-t border-white/10 bg-ink-surface/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionLabel num="02">After launch</SectionLabel>
        <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight">Website maintenance &amp; support charges</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {RETAINERS.map((r) => (
            <div key={r.name} className="rounded-[16px] border border-white/[0.12] bg-ink-bg p-6">
              <h3 className="text-lg font-semibold tracking-tight">{r.name}</h3>
              <div className="mt-2 text-2xl font-bold text-violet tabular-nums">{r.price}</div>
              <p className="mt-3 text-sm text-ink-muted leading-relaxed">{r.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MarketTable() {
  return (
    <section className="relative py-20 md:py-28 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionLabel num="03">Market comparison</SectionLabel>
        <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
          What agencies in Pune, Mumbai &amp; Nashik charge
        </h2>
        <p className="mt-6 text-lg text-ink-muted leading-relaxed max-w-3xl">{MARKET.intro}</p>
        <div className="mt-10 overflow-x-auto rounded-[16px] border border-white/[0.12]">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-ink-surface text-left text-[11px] uppercase tracking-[0.18em] text-ink-muted">
              <tr>
                <th scope="col" className="px-5 py-4 font-normal">Project</th>
                <th scope="col" className="px-5 py-4 font-normal">Budget / template</th>
                <th scope="col" className="px-5 py-4 font-normal">Mid-market agency</th>
                <th scope="col" className="px-5 py-4 font-normal text-white">NioDelta</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {MARKET.rows.map((r) => (
                <tr key={r.project}>
                  <th scope="row" className="px-5 py-4 text-left font-medium text-white">{r.project}</th>
                  <td className="px-5 py-4 text-ink-muted tabular-nums">{r.budget}</td>
                  <td className="px-5 py-4 text-ink-muted tabular-nums">{r.mid}</td>
                  <td className="px-5 py-4 text-white tabular-nums">{r.ours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-ink-muted">{MARKET.note}</p>
      </div>
    </section>
  );
}

function Factors() {
  return (
    <section className="relative py-20 md:py-28 border-t border-white/10 bg-ink-surface/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <SectionLabel num="04">Cost factors</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
            What changes the price
          </h2>
        </div>
        <ul className="md:col-span-8 divide-y divide-white/10 border-y border-white/10">
          {COST_FACTORS.map((f) => (
            <li key={f.k} className="py-6 grid grid-cols-1 md:grid-cols-12 gap-4">
              <h3 className="md:col-span-4 text-lg font-semibold tracking-tight">{f.k}</h3>
              <p className="md:col-span-8 text-ink-muted leading-relaxed">{f.v}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function PricingFaq() {
  return (
    <section className="relative py-20 md:py-28 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <SectionLabel num="05">FAQ</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">Pricing questions</h2>
          <div className="mt-8 text-sm text-ink-muted">
            Local pages:{' '}
            {CITIES.map((c, i) => (
              <React.Fragment key={c.slug}>
                {i > 0 && ' · '}
                <Link to={`${c.path}/`} className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white">
                  {c.name}
                </Link>
              </React.Fragment>
            ))}
          </div>
          <div className="mt-4 text-sm text-ink-muted">
            Building an app?{' '}
            <Link to="/mobile-app-development-cost/" className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white">
              Mobile app development cost
            </Link>
          </div>
          <div className="mt-8">
            <MagneticButton href={SITE.whatsapp} external variant="primary">
              Get a fixed quote <ArrowUpRight size={16} />
            </MagneticButton>
          </div>
        </div>
        <div className="md:col-span-8">
          <FAQList items={PRICING_FAQ} testIdPrefix="pricing-faq" />
        </div>
      </div>
    </section>
  );
}

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <FaqSchema items={PRICING_FAQ} />
      <PricingHero />
      <Plans />
      <Retainers />
      <MarketTable />
      <Factors />
      <PricingFaq />
      <CTABand
        eyebrow="Fixed quote in 48 hours"
        headline="Tell us the scope. We’ll send a number."
        sub="A 30-minute call on WhatsApp is enough to scope most websites. You get a written, fixed quote — no hourly surprises."
      />
    </div>
  );
}
